import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface PricingCardProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  buttonText?: string;
  popularLabel?: string;
}

export function PricingCard({ 
  title, 
  price, 
  description, 
  features, 
  isPopular,
  buttonText = "Começar Agora",
  popularLabel = "Mais Popular",
}: PricingCardProps) {
  return (
    <motion.div
      className="relative h-full"
      whileHover={{
        y: -6,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {isPopular && (
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-30 drop-shadow-xl">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <span className="relative px-7 py-2 rounded-full text-sm font-semibold shadow-2xl border border-black/30 bg-gradient-to-r from-primary to-blue-500 text-white whitespace-nowrap"
              style={{ background: 'linear-gradient(to right, var(--tw-gradient-stops)), #2563eb' }}>
              <span className="absolute inset-0 rounded-full bg-blue-600 opacity-60 -z-10"></span>
              {popularLabel}
            </span>
          </motion.div>
        </div>
      )}

      <div className={`glass-card p-5 sm:p-8 h-full flex flex-col group ${
        isPopular ? 'ring-2 ring-primary/30 shadow-xl' : ''
      }`}>
      <div className="text-center mb-4 sm:mb-8 flex-shrink-0">
        <h3 className="text-lg sm:text-xl font-semibold mb-1.5 sm:mb-4 md:h-14 flex items-center justify-center group-hover:text-primary transition-colors duration-300">
          {title}
        </h3>
        <div className="mb-2 sm:mb-4">
          <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent">
            {price}
          </span>
        </div>
        <p className="text-muted-foreground text-sm md:h-12 flex items-center justify-center leading-relaxed">
          {description}
        </p>
      </div>

      <div className="flex-grow mb-5 sm:mb-8">
        <ul className="space-y-2 sm:space-y-4">
          {features.map((feature, index) => (
            <motion.li 
              key={index} 
              className="flex items-start"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              <div className="rounded-full bg-primary/10 p-1 mr-3 mt-0.5 flex-shrink-0">
                <Check className="w-3 h-3 text-primary" strokeWidth={3} />
              </div>
              <span className="text-sm leading-relaxed">{feature}</span>
            </motion.li>
          ))}
        </ul>
      </div>
      
      <div className="mt-auto">
        <a 
          href="https://wa.me/5541996749435" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="block w-full"
        >
          <Button 
            variant="default"
            className={`w-full rounded-xl py-3 font-medium transition-all duration-300 ${
              isPopular 
                ? 'bg-gradient-to-r from-primary to-blue-500 hover:shadow-lg hover:shadow-primary/25' 
                : 'hover:bg-primary/90'
            }`}
          >
            {buttonText}
          </Button>
        </a>
      </div>
      
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </div>
    </motion.div>
  );
}
